import React, { useContext, useEffect, useMemo } from "react"
import { useLocation } from "react-router-dom"

import TriggerButton from "./TriggerButton"
import { FileChartPieIcon, FaIcon } from "../../../../../mp-core/icons"
import DropdownMenu, {
  DropdownMenuOptions
} from "../../../../generic-shared-components/components/DropdownMenu"
import Label from "./Label"

import { getProductLink, saveCurrentProductToSessionStorage } from "./helpers"

import { ProgramsContext } from "../ProgramsContext"
import { User } from "../../../../../mp-database"

import { useStyles } from "./styles"

interface Props {
  id?: string
  links: any
  pageName: string
  user: User
}

const ProductsDropDown = React.forwardRef<any, Props>((props, ref) => {
  const { id, links, pageName, user } = props
  const { classes } = useStyles()

  const location = useLocation()

  const { currentProgram } = useContext(ProgramsContext)

  const options = useMemo<DropdownMenuOptions[]>(() => {
    const options: DropdownMenuOptions[] = []

    if (links?.products?.length || links?.eBooks?.children?.length) {
      options.push(
        ...links?.products
          .map(link => ({
            link,
            productLink: getProductLink(link, currentProgram)
          }))
          .filter(
            ({ link, productLink }) =>
              link.url && productLink.key === currentProgram
          )
          .map(({ link, productLink }) => ({
            id: link.name,
            href:
              pageName === link.name
                ? `${location.pathname}${location.search}`
                : productLink.url,
            selected: pageName === link.name,
            label: (
              <Label
                name={link.name}
                subtitle={
                  link.hideChildName ? "" : link.children[0]?.name ?? ""
                }
              />
            ),
            "data-automation-id": "products-list"
          }))
      )
    }

    if (links?.eBooks?.url && links?.eBooks?.children?.length) {
      const programBooks = links.eBooks.children[0].children.filter(link => {
        // If currentProgram doesn't exist we are probably on profile page
        if (!currentProgram && pageName === "--") {
          return true // Show eBook product if on profile page
        }
        return link.key.toUpperCase() === currentProgram?.toUpperCase()
      })

      if (programBooks.length > 0) {
        options.push({
          id: "ebooks",
          label: links.eBooks.name,
          href: links.eBooks.url,
          selected: pageName === links?.eBooks?.name,
          "data-automation-id": "products-list"
        })
      }
    }

    const hasActReports =
      links?.reports?.url && currentProgram?.toUpperCase().includes("ACT")

    const hasAdminLinks =
      (links?.manage?.url && links?.manage?.children?.length) ||
      links?.contentAuthoring?.url ||
      hasActReports ||
      links?.schoolSupport?.url ||
      links?.contextHelpManager?.url

    if (options.length > 0 && hasAdminLinks) {
      options.push({ id: "divider-1", divider: true })
    }

    if (links?.manage?.url && links?.manage?.children?.length) {
      options.push({
        id: "manage",
        label: links.manage.name,
        href: links.manage.url,
        selected: pageName === links.manage.name,
        icon: <FaIcon solid name="users" />,
        "data-automation-id": "products-list"
      })
    }

    if (links?.contentAuthoring?.url) {
      options.push({
        id: "content-authoring",
        label: links.contentAuthoring.name,
        href: links.contentAuthoring.url,
        selected: pageName === links.contentAuthoring.name,
        icon: <FaIcon solid name="pen-to-square" />,
        "data-automation-id": "products-list"
      })
    }

    if (hasActReports) {
      options.push({
        id: "reports",
        label: links.reports.name,
        href: links.reports.url,
        selected: pageName === links.reports.name,
        icon: <FaIcon solid name="chart-simple" />,
        "data-automation-id": "products-list"
      })
    }

    if (links?.schoolSupport?.url) {
      options.push({
        id: "school-support",
        label: links.schoolSupport.name,
        href: links.schoolSupport.url,
        selected: pageName === links.schoolSupport.name,
        icon: <FileChartPieIcon />,
        "data-automation-id": "products-list"
      })
    }

    if (links?.contextHelpManager?.url) {
      options.push({
        id: "context-help-manager",
        label: links.contextHelpManager.name,
        href: links.contextHelpManager.url,
        selected: pageName === links.contextHelpManager.name,
        icon: <FileChartPieIcon />,
        "data-automation-id": "products-list"
      })
    }

    return options
  }, [links, pageName, location, currentProgram, user.role])

  useEffect(() => {
    saveCurrentProductToSessionStorage(pageName)
  }, [pageName])

  const isSingleProduct = options.length === 1 && pageName !== "--"

  return (
    <>
      <DropdownMenu
        ref={ref}
        id="products-dropdown"
        anchor={
          <TriggerButton
            id={id}
            links={links}
            pageName={pageName}
            disabled={isSingleProduct}
          />
        }
        options={options}
        anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
        transformOrigin={{ horizontal: "left", vertical: "top" }}
        classes={{
          paper: classes.paper,
          menuList: classes.menuList,
          menuItem: classes.menuListItem
        }}
        wrapperClassName={classes.mainLinksDropDown}
      />
    </>
  )
})

export default ProductsDropDown
